import type {
    PtV2Action,
    PtV2ActionInput,
    PtV2HarmonizationRow,
    PtV2ImportError,
    PtV2ImportRecord,
    PtV2LeakageRow,
    PtV2PlanItem,
    PtV2TermMixItem,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {formatEurFull, termDays} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";

/** The corporate baseline the analysis measures against. */
export const BASELINE_DAYS = 60;

export const HARMONIZATION_TARGETS = [45, 60, 90] as const;
export type PtV2TargetDays = typeof HARMONIZATION_TARGETS[number];

/**
 * Annualised return of taking an early-payment discount.
 * apr = d / (1 − d) × 365 / (net − discountDays), with d as a fraction (0.02 for 2%).
 * Null when the discount window does not end before the net term, or the discount is not in [0, 1).
 */
export function discountApr(discountPct: number, discountDays: number, netDays: number): number | null {
    const d = discountPct / 100;
    const window = netDays - discountDays;
    if (window <= 0 || d < 0 || d >= 1) return null;
    return d / (1 - d) * 365 / window;
}

/** Working capital freed by moving a supplier to the target term: spend × max(0, target − current) ÷ 365. */
export function harmonizationWc(spend: number, currentDays: number, targetDays: number): number {
    return spend * Math.max(0, targetDays - currentDays) / 365;
}

/**
 * What working capital is worth a year at the customer's cost of capital: working capital × rate ÷ 100.
 * Null without a rate (not set in the Payment Terms settings) or without a working-capital figure.
 */
export function annualValue(workingCapital: number | null | undefined, costOfCapitalPct: number | null): number | null {
    if (workingCapital == null || costOfCapitalPct == null) return null;
    return workingCapital * costOfCapitalPct / 100;
}

/** "8.5%" for a rate, trailing zeros dropped. */
export function rateLabel(pct: number): string {
    return `${Number(pct.toFixed(2))}%`;
}

/** Slugify a supplier name the way the importer does for rows without an ERP id. */
export function slugifySupplier(name: string): string {
    return name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export type PtV2PlanGroupKey = 'quick_win' | 'category_strategy' | 'discount_automation';

export type PtV2PlanGroup = {
    group: PtV2PlanGroupKey;
    title: string;
    subtitle: string;
    items: PtV2PlanItem[];
};

const DISCOUNT_TASKS: PtV2PlanItem[] = [
    {
        key: 'recommended:discount_automation:net10-2pct',
        kind: 'recommended',
        title: 'Add a scanning rule for “Net 10 days 2%”',
        subtitle: null,
        value: null,
        status: 'open',
    },
    {
        key: 'recommended:discount_automation:surcharge-2pct',
        kind: 'recommended',
        title: 'Add a scanning rule for late-payment surcharges (kredietbeperking 2%)',
        subtitle: null,
        value: null,
        status: 'open',
    },
    {
        key: 'recommended:discount_automation:7-day-run',
        kind: 'recommended',
        title: 'Set up a 7-day payment run for flagged invoices',
        subtitle: null,
        value: null,
        status: 'open',
    },
];

/** Only the fields `POST actions/` takes; plan items carry a client-only `source`. */
export function toActionInput(item: PtV2ActionInput): PtV2ActionInput {
    return {key: item.key, kind: item.kind, title: item.title, subtitle: item.subtitle, value: item.value, status: item.status};
}

/**
 * The plan item for a leakage supplier. One key per supplier and opportunity (`leakage:<supplierKey>`), so the
 * drawer and the quick-win row share the action and its initiative; the plan group is display only.
 */
export function leakageActionInput(row: PtV2LeakageRow): PtV2PlanItem {
    return {
        key: `leakage:${row.supplierKey}`,
        kind: 'leakage',
        title: `${row.supplier} → ${row.recommendedCode} in the vendor master`,
        subtitle: 'Payment term leakage',
        value: row.wcTrapped,
        status: 'open',
        source: {
            supplier: row.supplier,
            currentTerm: row.erpTerm,
            targetCode: row.recommendedCode,
            value: row.wcTrapped,
            recommendedAction: row.recommendedAction,
            tab: 'leakage',
        },
    };
}

/**
 * The plan item for a harmonization supplier moving to `targetCode`; `row` carries the gain at that target.
 * Keyed `harmonization:<supplierKey>` whatever the target, shared by the drawer and the category-strategy row.
 */
export function harmonizationActionInput(row: PtV2HarmonizationRow, targetCode: string): PtV2PlanItem {
    return {
        key: `harmonization:${row.supplierKey}`,
        kind: 'harmonization',
        title: `${row.supplier}: renegotiate ${row.currentTerm} → ${targetCode}`,
        subtitle: 'Term harmonization',
        value: row.wcFreed,
        status: 'open',
        source: {
            supplier: row.supplier,
            currentTerm: row.currentTerm,
            targetCode,
            value: row.wcFreed,
            recommendedAction: null,
            tab: 'harmonization',
        },
    };
}

/** The harmonization target: the workpaper's per-supplier target, or one term for every supplier. */
export type PtV2HarmonizationTarget = 'supplier' | PtV2TargetDays;
export const HARMONIZATION_TARGET_OPTIONS: PtV2HarmonizationTarget[] = ['supplier', ...HARMONIZATION_TARGETS];

export function harmonizationTargetLabel(target: PtV2HarmonizationTarget): string {
    return target === 'supplier' ? 'Per supplier' : `NT${target}`;
}

/**
 * A harmonization row at the chosen target; `targetCode` is null when the workpaper names no target.
 * `wcSource` says where `wcFreed` comes from: the workpaper's `liquidityUnlock`, or spend × gain ÷ 365.
 */
export type PtV2HarmonizationView = PtV2HarmonizationRow & {targetCode: string | null; wcSource: 'workpaper' | 'computed'};

/**
 * Rows at the chosen target. `NT45/60/90` keeps the API scenario (`?target_days=`) and shows that term on every row.
 * Per supplier: gain = workpaper target days − current days (none below zero), and working capital is the
 * workpaper's `liquidityUnlock`, else spend × gain ÷ 365.
 */
export function harmonizationView(rows: PtV2HarmonizationRow[], target: PtV2HarmonizationTarget): PtV2HarmonizationView[] {
    if (target !== 'supplier') return rows.map(r => ({...r, targetCode: `NT${target}`, wcSource: 'computed'}));
    return rows.map(r => {
        const days = r.targetDaysWorkpaper ?? termDays(r.targetTerm);
        const shiftDays = days == null ? 0 : Math.max(0, days - r.currentDays);
        return {
            ...r,
            targetCode: r.targetTerm ?? (days != null ? `NT${days}` : null),
            shiftDays,
            wcFreed: r.liquidityUnlock ?? harmonizationWc(r.spend, r.currentDays, r.currentDays + shiftDays),
            wcSource: r.liquidityUnlock != null ? 'workpaper' : 'computed',
        };
    });
}

/**
 * The drawer's working-capital line for a row, so it always shows the same figure as the grid and the KPI.
 * From the workpaper: its value, with spend × days ÷ 365 as an approximate cross-check. Computed: the formula and its result.
 */
export function harmonizationCalculation(row: PtV2HarmonizationView): {
    label: string;
    value: number;
    formula: string;
} {
    const formula = `${formatEurFull(row.spend)} × ${row.shiftDays} ÷ 365`;
    if (row.wcSource === 'workpaper') {
        const check = harmonizationWc(row.spend, row.currentDays, row.currentDays + row.shiftDays);
        return {label: 'From the Hermes workpaper', value: row.wcFreed, formula: `${formula} ≈ ${formatEurFull(check)}`};
    }
    return {label: 'Calculation', value: row.wcFreed, formula: `${formula} = ${formatEurFull(row.wcFreed)}`};
}

/**
 * The request a plan checkbox sends. An existing action only gets its status (`PATCH actions/{id}/`), so the title and
 * value stored by a drawer or an initiative stay; a new key is upserted with the plan item.
 */
export function actionStatusRequest(
    item: PtV2ActionInput,
    existing: Pick<PtV2Action, 'id'> | undefined,
    status: PtV2Action['status'],
): {method: 'PATCH'; id: number; data: {status: PtV2Action['status']}} | {method: 'POST'; data: PtV2ActionInput} {
    return existing
        ? {method: 'PATCH', id: existing.id, data: {status}}
        : {method: 'POST', data: {...toActionInput(item), status}};
}

/**
 * The recommended plan: the top 5 leakage suppliers by trapped working capital, the top 3 NT30
 * harmonization suppliers (at the baseline target), and the fixed discount-automation tasks.
 */
export function buildPlanGroups(leakage: PtV2LeakageRow[], harmonizationAtBaseline: PtV2HarmonizationRow[]): PtV2PlanGroup[] {
    const quick = [...leakage]
        .sort((a, b) => b.wcTrapped - a.wcTrapped)
        .slice(0, 5)
        .map(r => leakageActionInput(r));
    const category = harmonizationAtBaseline
        .filter(r => r.currentTerm.toUpperCase() === 'NT30')
        .sort((a, b) => b.wcFreed - a.wcFreed)
        .slice(0, 3)
        .map(r => harmonizationActionInput(r, `NT${BASELINE_DAYS}`));
    return [
        {
            group: 'quick_win',
            title: 'Quick win: align the ERP vendor master',
            subtitle: 'Set the SoftCo and SAP default terms to the signed contract term for the top mismatched vendors.',
            items: quick,
        },
        {
            group: 'category_strategy',
            title: 'Category strategy: renegotiate NT30 suppliers',
            subtitle: 'Open renegotiations to move the largest Net 30 suppliers to the Net 60 baseline.',
            items: category,
        },
        {
            group: 'discount_automation',
            title: 'Discount automation',
            subtitle: 'Configure AP scanning rules so discount terms go to an accelerated payment run.',
            items: DISCOUNT_TASKS,
        },
    ];
}

/** Actions the user added that are not part of the recommended plan, newest first. */
export function userAddedActions(actions: PtV2Action[], groups: PtV2PlanGroup[]): PtV2Action[] {
    const recommended = new Set(groups.flatMap(g => g.items.map(i => i.key)));
    return actions
        .filter(a => !recommended.has(a.key))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.id - a.id);
}

/**
 * A supplier's term mix, largest share first, with shares that sum to 1.
 * Uses the backend `share` when the shares are usable, else recomputes them from spend.
 */
export function termMixShares(mix: PtV2TermMixItem[]): PtV2TermMixItem[] {
    if (mix.length === 0) return [];
    const spend = mix.reduce((s, m) => s + Math.max(0, m.spend), 0);
    const given = mix.reduce((s, m) => s + Math.max(0, m.share ?? 0), 0);
    const items = mix.map(m => ({
        ...m,
        share: given > 0 ? Math.max(0, m.share ?? 0) / given : spend > 0 ? Math.max(0, m.spend) / spend : 1 / mix.length,
    }));
    return items.sort((a, b) => b.share - a.share);
}

/**
 * Sync and import history for display: the latest successful run, the earlier successful ones and the failed
 * attempts. Input is newest first, as `GET imports/` returns it.
 */
export function splitRunHistory(runs: PtV2ImportRecord[]) {
    const succeeded = runs.filter(r => r.status === 'succeeded');
    return {
        latest: succeeded[0] ?? null,
        earlier: succeeded.slice(1),
        failed: runs.filter(r => r.status !== 'succeeded'),
    };
}

/**
 * Normalise a 400 body from `POST import/` into a flat list. Accepts
 * `{errors: {file: [msg]}}`, `{errors: [{file, message}]}`, `{file: [msg]}` or `{detail}`.
 */
export function parseImportErrors(body: unknown): PtV2ImportError[] {
    if (body == null) return [];
    if (typeof body === 'string') return [{file: null, message: body}];
    if (Array.isArray(body)) {
        return body.flatMap(item => {
            if (typeof item === 'string') return [{file: null, message: item}];
            if (item && typeof item === 'object') {
                const o = item as Record<string, unknown>;
                const message = String(o.message ?? o.error ?? o.detail ?? JSON.stringify(o));
                const row = o.row != null ? ` (row ${String(o.row)})` : '';
                return [{file: o.file != null ? String(o.file) : null, message: message + row}];
            }
            return [];
        });
    }
    if (typeof body !== 'object') return [{file: null, message: String(body)}];
    const o = body as Record<string, unknown>;
    if ('errors' in o) {
        const nested = parseImportErrors(o.errors);
        if (typeof o.detail === 'string') nested.unshift({file: null, message: o.detail});
        return nested;
    }
    return Object.entries(o).flatMap(([key, v]) => {
        const file = key === 'detail' || key === 'non_field_errors' || key === 'nonFieldErrors' ? null : key;
        const messages = Array.isArray(v) ? v : [v];
        return messages.map(m => ({file, message: typeof m === 'string' ? m : JSON.stringify(m)}));
    });
}
